/**
 * Static copy of My Candidates - Retention CandidatesFilterUtils.
 * Same structure, static options - no Redux, no API.
 */
import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { calculateTreeCount } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';

/** Set to true to temporarily hide Placement Recruiter filter; only Phone number filter shown. */
export const HIDE_RECRUITER_FILTER_FOR_SMS = true;

export enum DashboardRetentionFilterName {
  placementRecruiter = 'placementRecruiter',
  smsSearchType = 'smsSearchType',
  senderPhoneNumber = 'senderPhoneNumber',
  discipline = 'discipline',
  specialty = 'specialty',
  states = 'states',
  candidateStatus = 'candidateStatus',
  rebooked = 'rebooked',
  bookedOutOfRange = 'bookedOutOfRange',
  placementStatus = 'placementStatus',
}

export const defaultRetentionFilterValues = {
  placementRecruiter: null as { id: number; name: string; value: string } | null,
  discipline: [] as { id: number; name: string; value: string }[],
  specialty: [] as { id: number; name: string; value: string }[],
  states: 'all' as string,
  candidateStatus: [] as { id: number; name: string; value: string }[],
  rebooked: 'all' as string,
  bookedOutOfRange: 'all' as string,
  placementStatus: [] as any[],
};

const STATIC_OPTIONS = {
  discipline: [{ id: 0, name: 'All Disciplines', value: 'All' }],
  specialty: [{ id: 0, name: 'All Specialties', value: 'All' }],
  states: [{ id: 0, name: 'All States', value: 'all' }],
  candidateStatus: [{ id: 0, name: 'All', value: 'All' }],
  rebooked: [
    { id: 0, name: 'All', value: 'all' },
    { id: 1, name: 'Yes', value: 'Y' },
    { id: 2, name: 'No', value: 'N' },
  ],
  bookedOutOfRange: [
    { id: 0, name: 'All', value: 'all' },
    { id: 1, name: 'Yes', value: 'Y' },
    { id: 2, name: 'No', value: 'N' },
  ],
  placementStatus: [] as any[],
};

/** SMS tab — Placement Recruiter + Phone Number. Automatically searches partition key, sender, and recipient (tos). */
export const getSmsRecruiterFilterSpecs = (
  recruiterOptions: { id: number; name: string; value: number }[],
  options?: { hideRecruiterFilter?: boolean },
): any[] => {
  const hideRecruiter = options?.hideRecruiterFilter ?? false;
  const allSpecs = [
    {
      filterName: 'Placement Recruiter',
      name: DashboardRetentionFilterName.placementRecruiter,
      type: FilterCompTypes.TYPEAHEADDROPDOWN,
      isMultiSelect: false,
      options: recruiterOptions,
      fallbackPlacements: ['top-start', 'bottom-start', 'right'],
      applyOnClickAway: true,
    },
    {
      filterName: 'Phone number',
      name: DashboardRetentionFilterName.senderPhoneNumber,
      type: FilterCompTypes.TEXTSEARCH,
      placeholder: 'Phone (e.g. +1234567890)',
      fallbackPlacements: ['top-start', 'bottom-start', 'right'],
      applyOnClickAway: true,
    },
  ];
  return hideRecruiter ? allSpecs.filter(s => s.name !== DashboardRetentionFilterName.placementRecruiter) : allSpecs;
};

/** Default values for SMS tab filters. */
export const defaultSmsFilterValues = {
  placementRecruiter: null as { id: number; name: string; value: number } | null,
  senderPhoneNumber: '' as string,
};

export const getDashboardRetentionFilterSpecs = (): any[] => [
  {
    filterName: 'Placement Recruiter',
    name: DashboardRetentionFilterName.placementRecruiter,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: [],
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Discipline',
    name: DashboardRetentionFilterName.discipline,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: true,
    options: STATIC_OPTIONS.discipline,
    selectAllValue: 'All',
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Specialty',
    name: DashboardRetentionFilterName.specialty,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: true,
    options: STATIC_OPTIONS.specialty,
    selectAllValue: 'All',
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'States',
    name: DashboardRetentionFilterName.states,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: STATIC_OPTIONS.states,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Candidate Status',
    name: DashboardRetentionFilterName.candidateStatus,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: true,
    options: STATIC_OPTIONS.candidateStatus,
    selectAllValue: 'All',
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Rebooked',
    name: DashboardRetentionFilterName.rebooked,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: STATIC_OPTIONS.rebooked,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Booked out of range',
    name: DashboardRetentionFilterName.bookedOutOfRange,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: STATIC_OPTIONS.bookedOutOfRange,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Placement Status',
    name: DashboardRetentionFilterName.placementStatus,
    type: FilterCompTypes.TREE,
    isMultiSelect: true,
    options: STATIC_OPTIONS.placementStatus,
    placeholder: 'Select',
    version2: true,
    returnsObjectAsValue: true,
    size: 'large',
    customSelectedCount: (value: any) => calculateTreeCount(value),
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
    isSelectAll: true,
  },
];

const EXTERNAL_TASK_STATUS_OPTIONS = [
  { id: 0, name: 'All Status', value: '' },
  { id: 1, name: 'New', value: '1' },
  { id: 2, name: 'Pending', value: '2' },
  { id: 3, name: 'In Progress', value: '3' },
  { id: 4, name: 'Completed', value: '4' },
  { id: 5, name: 'Cancelled', value: '5' },
  { id: 6, name: 'On Hold', value: '6' },
];

/** External Tasks: Task Name, Placement ID, Candidate ID, Candidate Name, Status */
export const getDashboardExternalTasksFilterSpecs = (): any[] => [
  {
    filterName: 'Task Name',
    name: 'search',
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Task Name',
    applyOnClickAway: false,
  },
  {
    filterName: 'Placement ID',
    name: 'placementId',
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Placement ID',
    applyOnClickAway: false,
  },
  {
    filterName: 'Candidate ID',
    name: 'candidateId',
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Candidate ID',
    applyOnClickAway: false,
  },
  {
    filterName: 'Candidate Name',
    name: 'candidateName',
    type: FilterCompTypes.TEXTSEARCH,
    placeholder: 'Candidate Name',
    applyOnClickAway: false,
  },
  {
    filterName: 'Status',
    name: 'statusIds',
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: EXTERNAL_TASK_STATUS_OPTIONS,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Date Range',
    name: 'dateRange',
    type: FilterCompTypes.DATERANGEV2,
    variant: 'outlined',
    secondaryLabel: 'Date Range',
    useMaxWidth: true,
    maxWidth: 210,
    version3: true,
    clickAway: true,
    disablePortal: true,
    quickSelect: false,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
];

export const defaultExternalTasksFilterValues = {
  search: '',
  placementId: '',
  candidateId: '',
  candidateName: '',
  statusIds: null as { id: number; name: string; value: string } | null,
  dateRange: null as { startDate?: string; endDate?: string } | null,
};
